<template>
  <div class="delivery-info-section">
    <h6 class="delivery-info-section__title">
      Special Request Delivery Info
    </h6>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Store Name*</label>
      <SanitizedInput
        v-model="info.storeName"
        :disabled="disabled"
        placeholder="Please input name of store"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Store Phone Number</label>
      <phone-number-input
        id="special-request-store-phone"
        v-model="info.storePhoneNumber"
        :disabled="disabled"
        :show-live-call="false"
      />
      <div class="delivery-info-section__help">
        Leave empty to fall back to the caller's phone number.
      </div>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Is the store open?*</label>
      <el-select
        v-model="info.storeOpen"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in yesNoOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Item type*</label>
      <el-select
        v-model="info.itemType"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in specialRequestItemTypes"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Item description*</label>
      <SanitizedInput
        v-model="info.itemDescription"
        :disabled="disabled"
        placeholder="Please input description of item"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Paid For*</label>
      <el-select
        v-model="info.paidFor"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in specialRequestPaidOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Item Count*</label>
      <el-input-number
        v-model="info.itemCount"
        :disabled="disabled"
        :min="1"
        :step="1"
        controls-position="right"
        class="delivery-info-section__control"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Order Value($)*</label>
      <el-input-number
        v-model="info.orderValueDollars"
        :disabled="disabled"
        :min="0.01"
        :step="0.01"
        :precision="2"
        controls-position="right"
        class="delivery-info-section__control"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Recipient Name*</label>
      <SanitizedInput
        v-model="info.recipientName"
        :disabled="disabled"
        placeholder="Please input name of recipient"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Pickup Instructions</label>
      <SanitizedTextarea
        v-model="info.pickupInstructions"
        :disabled="disabled"
        rows="3"
        placeholder="Explain how we will need to pick up the item"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Delivery Options*</label>
      <el-select
        v-model="info.deliveryOptions"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in specialRequestOrPharmacyDeliveryOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Delivery Instructions</label>
      <SanitizedTextarea
        v-model="info.deliveryInstructions"
        :disabled="disabled"
        rows="3"
        placeholder="Explain how we will need to deliver the item"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Driver Tip($)</label>
      <el-input-number
        v-model="info.driverTipDollars"
        :disabled="disabled"
        :min="0"
        :step="0.01"
        :precision="2"
        controls-position="right"
        class="delivery-info-section__control"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Notes for LP</label>
      <SanitizedTextarea
        v-model="info.notesForLP"
        :disabled="disabled"
        rows="3"
        placeholder="Additional notes for Loss & Prevention"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { yesNoOptions } from '@/constants/app';
import {
  specialRequestItemTypes,
  specialRequestOrPharmacyDeliveryOptions,
  specialRequestPaidOptions,
} from '@/constants/ride';
import { YesNoEnum } from '@/models/app';
import { RideServiceType, type DeliveryEstimatePayload, type DeliveryInfoFormResult, type SpecialRequestDeliveryInfo } from '@/models/ride';
import {
  buildSharedDeliveryEstimatePayload,
  type DeliveryValidationResult,
} from '@/Utils/deliveryInfoForm';

const info = defineModel<SpecialRequestDeliveryInfo>({ required: true });

withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  (event: 'update:result', value: DeliveryInfoFormResult): void;
}>();


const isFilledString = (value: string | null | undefined): boolean => Boolean(value && value.trim());

const getValidationErrors = (info: SpecialRequestDeliveryInfo): Record<string, string> => {
  const nextErrors: Record<string, string> = {};

  if (!isFilledString(info.storeName)) {
    nextErrors.storeName = 'Store Name is required';
  }
  if (!info.storeOpen) {
    nextErrors.storeOpen = 'Store open status is required';
  } else if (info.storeOpen !== YesNoEnum.Yes) {
    nextErrors.storeOpen = 'The store must be open to place this delivery';
  }
  if (!info.itemType) {
    nextErrors.itemType = 'Item type is required';
  }
  if (!isFilledString(info.itemDescription)) {
    nextErrors.itemDescription = 'Item description is required';
  }
  if (!info.paidFor) {
    nextErrors.paidFor = 'Paid For is required';
  } else if (info.paidFor === 'No') {
    nextErrors.paidFor = 'Unpaid special requests cannot be delivered';
  }
  if (!info.itemCount || info.itemCount < 1) {
    nextErrors.itemCount = 'Item Count must be at least 1';
  }
  if (!info.orderValueDollars || info.orderValueDollars <= 0) {
    nextErrors.orderValueDollars = 'Order Value is required';
  }
  if (!isFilledString(info.recipientName)) {
    nextErrors.recipientName = 'Recipient Name is required';
  }
  if (!info.deliveryOptions) {
    nextErrors.deliveryOptions = 'Delivery Options is required';
  }

  return nextErrors;
};

const buildEstimatePayload = (info: SpecialRequestDeliveryInfo): DeliveryEstimatePayload | null => {
  const shared = buildSharedDeliveryEstimatePayload({info, deliveryType: RideServiceType.SpecialRequest});
  if (!shared) {
    return null;
  }

  return {
    ...shared,
    itemType: info.itemType,
    itemDescription: info.itemDescription.trim(),
    recipientName: info.recipientName.trim(),
  };
};

const buildResult = (info: SpecialRequestDeliveryInfo): DeliveryInfoFormResult => {
  const validationErrors = getValidationErrors(info);
  const isValid = Object.keys(validationErrors).length === 0;

  return {
    isValid,
    payload: isValid ? buildEstimatePayload(info) : null,
    notesForLP: info.notesForLP,
  };
};

const result = computed(() => buildResult(info.value));

const validate = (): DeliveryValidationResult => {
  const errors = Object.values(getValidationErrors(info.value));
  emit('update:result', result.value);
  return {
    isValid: errors.length === 0,
    errors,
  };
};

watch(
  result,
  (value) => {
    emit('update:result', value);
  },
  { immediate: true, deep: true },
);

defineExpose({
  validate,
});
</script>
